import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "../../components/common/PageHeader";
import { ReportsService, AdminSalesService } from "../../services";
import { currency, dateTime } from "../../utils/format";
import { addDays, currentBusinessDate, isInBusinessDay } from "../../utils/businessDay";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import {
  RefreshCw,
  Download,
  BadgeDollarSign,
  ShoppingCart,
  TrendingUp,
  Percent,
  Wine,
  Utensils,
  FileText,
} from "lucide-react";

const formatPercent = (value) => {
  if (!Number.isFinite(value)) {
    return "0.0%";
  }

  return `${value.toFixed(1)}%`;
};

const escapeHtml = (value) => {
  if (value === null || value === undefined) {
    return "—";
  }

  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
};

const buildProfitReportFileName = (date) => {
  return `Profit_Report_${date}`;
};

const MetricTile = ({ label, value, icon: Icon, tone = "slate", loading }) => {
  const toneClasses = {
    navy: "from-navy-700 to-slate-900 text-white",
    emerald: "from-emerald-600 to-teal-700 text-white",
    rose: "from-rose-600 to-red-700 text-white",
    amber: "from-amber-500 to-orange-600 text-white",
    gold: "from-yellow-500 to-amber-600 text-white",
    slate: "from-slate-700 to-slate-900 text-white",
  };

  return (
    <div
      className={`rounded-2xl bg-gradient-to-br ${
        toneClasses[tone] || toneClasses.slate
      } p-5 shadow-sm`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-white/70">
            {label}
          </div>
          <div className="font-display text-2xl font-bold mt-2">
            {loading ? "…" : value}
          </div>
        </div>

        {Icon && (
          <div className="rounded-xl bg-white/15 p-2.5">
            <Icon className="h-5 w-5" />
          </div>
        )}
      </div>
    </div>
  );
};

const ProfitBreakdownPanel = ({
  title,
  subtitle,
  sales,
  cost,
  profit,
  icon: Icon,
  accentClass,
  breakdown = [],
  showMargin = true,
}) => {
  const margin = showMargin && sales > 0 ? (profit / sales) * 100 : 0;

  return (
    <div className="bms-card overflow-hidden">
      <div className={`h-1.5 ${accentClass}`} />

      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              {Icon && (
                <div className="rounded-xl bg-slate-100 p-2">
                  <Icon className="h-4 w-4 text-slate-700" />
                </div>
              )}

              <h3 className="font-display text-lg font-semibold text-slate-900">
                {title}
              </h3>
            </div>

            <p className="text-xs text-slate-500 mt-2">{subtitle}</p>
          </div>

          {showMargin && (
            <span
              className={`rounded-full px-3 py-1 text-xs font-semibold ${
                profit < 0
                  ? "bg-rose-50 text-rose-700"
                  : "bg-emerald-50 text-emerald-700"
              }`}
            >
              {formatPercent(margin)}
            </span>
          )}
        </div>

        <div className="mt-5 grid grid-cols-1 gap-3">
          <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
            <span className="text-sm text-slate-500">Sales</span>
            <span className="font-display font-semibold text-slate-900">
              {currency(sales)}
            </span>
          </div>

          {breakdown.map((row) => (
            <div key={row.label} className="flex items-center justify-between px-4 text-xs text-slate-500">
              <span>{row.label}</span>
              <span className="font-semibold text-slate-700">{currency(row.value)}</span>
            </div>
          ))}

          <div className="flex items-center justify-between rounded-xl bg-rose-50 px-4 py-3">
            <span className="text-sm text-rose-700">Cost</span>
            <span className="font-display font-semibold text-rose-700">
              {currency(cost)}
            </span>
          </div>

          <div className="flex items-center justify-between rounded-xl bg-emerald-50 px-4 py-3">
            <span className="text-sm text-emerald-700">Profit</span>
            <span
              className={`font-display text-lg font-bold ${
                profit < 0 ? "text-rose-700" : "text-emerald-700"
              }`}
            >
              {currency(profit)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ProfitDashboardPage = () => {
  const [date, setDate] = useState(currentBusinessDate());
  const [data, setData] = useState({ profit: {}, sales: [] });
  const [loading, setLoading] = useState(true);

  const load = (d) => {
    setLoading(true);

    Promise.all([ReportsService.profit(d), AdminSalesService.list()])
      .then(([profit, sales]) => setData({ profit, sales }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(date);
    // eslint-disable-next-line
  }, [date]);

  const profit = data.profit || {};

  const bottleSales = Number(profit.totalBottleSales || 0);
  const bottleCost = Number(profit.totalBottleCost || 0);
  const bottleProfit = Number(profit.totalBottleProfit || 0);
  const cigaretteSales = Number(profit.cigaretteSales || 0);
  const liquorSales = Number(profit.liquorSales ?? bottleSales - cigaretteSales);

  const restaurantSales = Number(profit.totalRestaurantSales || 0);
  const restaurantCost = Number(profit.totalRestaurantCost || 0);
  const restaurantProfit = Number(profit.totalRestaurantProfit || 0);

  const restaurantViaBarSales = Number(profit.restaurantViaBarSales || 0);
  const restaurantViaBarCost = Number(profit.restaurantViaBarCost || 0);
  const restaurantViaBarProfit = Number(profit.restaurantViaBarProfit || 0);
  const restaurantDirectSales = Number(profit.restaurantDirectSales || 0);
  const restaurantDirectCost = Number(profit.restaurantDirectCost || 0);
  const restaurantDirectProfit = Number(profit.restaurantDirectProfit || 0);

  const totalSales = Number(
    profit.totalSales ?? bottleSales + restaurantSales
  );
  const totalCost = Number(profit.totalCost ?? bottleCost + restaurantCost);
  const totalProfit = Number(
    profit.totalProfit ?? bottleProfit + restaurantProfit
  );

  const margin = totalSales > 0 ? (totalProfit / totalSales) * 100 : 0;
  const generatedOn = dateTime(new Date());

  const trend = useMemo(() => {
    const today = currentBusinessDate();
    const days = Array.from({ length: 14 }, (_, i) => addDays(today, i - 13));

    return days.map((d) => {
      let revenue = 0;
      let profitVal = 0;

      (data.sales || []).forEach((s) => {
        if (
          !isInBusinessDay(s.createdAt || s.saleDateTime, d) ||
          s.status === "CANCELLED"
        ) {
          return;
        }

        revenue += Number(s.grandTotal || s.grandTotalAmount || 0);

        profitVal += Number(
          s.profit ||
            s.totalProfit ||
            s.totalBottleProfit ||
            s.totalRestaurantProfit ||
            0
        );
      });

      return { day: d.slice(5), revenue, profit: profitVal };
    });
  }, [data.sales]);

  const printProfitReportPdf = () => {
    const reportFileName = buildProfitReportFileName(date);

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${escapeHtml(reportFileName)}</title>

          <style>
            @page {
              size: A4 portrait;
              margin: 10mm;
            }

            * {
              box-sizing: border-box;
            }

            body {
              margin: 0;
              padding: 0;
              background: #ffffff;
              color: #0f172a;
              font-family: Arial, Helvetica, sans-serif;
              font-size: 11px;
            }

            .report-page {
              width: 100%;
              padding: 0;
            }

            .hero {
              background: linear-gradient(135deg, #0f1f46, #047857, #0f172a);
              color: #ffffff;
              border-radius: 16px;
              padding: 18px 20px;
              margin-bottom: 14px;
            }

            .hero-top {
              display: flex;
              justify-content: space-between;
              gap: 18px;
              align-items: flex-start;
            }

            .eyebrow {
              font-size: 9px;
              text-transform: uppercase;
              letter-spacing: 0.14em;
              color: rgba(255, 255, 255, 0.7);
              font-weight: 800;
            }

            .title {
              margin: 6px 0 0;
              font-size: 24px;
              line-height: 1.1;
              font-weight: 800;
            }

            .subtitle {
              margin-top: 6px;
              font-size: 10.5px;
              color: rgba(255, 255, 255, 0.78);
            }

            .generated {
              min-width: 160px;
              text-align: right;
              font-size: 9.5px;
              color: rgba(255, 255, 255, 0.7);
              line-height: 1.35;
            }

            .generated strong {
              display: block;
              color: #ffffff;
              font-size: 10.5px;
              margin-top: 3px;
            }

            .summary-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 8px;
              margin-bottom: 14px;
            }

            .summary-card {
              border: 1px solid #dbe3ef;
              border-radius: 12px;
              padding: 10px 12px;
              background: #ffffff;
            }

            .summary-card.profit {
              background: #ecfdf5;
              border-color: #bbf7d0;
            }

            .summary-card.cost {
              background: #fff1f2;
              border-color: #fecdd3;
            }

            .summary-card.margin {
              background: #fffbeb;
              border-color: #fde68a;
            }

            .card-label {
              font-size: 8.8px;
              color: #475569;
              text-transform: uppercase;
              letter-spacing: 0.08em;
              font-weight: 800;
            }

            .card-value {
              margin-top: 5px;
              font-size: 16px;
              font-weight: 800;
              color: #0f172a;
            }

            .card-value.profit-value {
              color: ${totalProfit < 0 ? "#be123c" : "#047857"};
            }

            .section-title {
              font-size: 13px;
              font-weight: 800;
              color: #0f172a;
              margin: 14px 0 8px;
            }

            .breakdown-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 10px;
              margin-bottom: 14px;
            }

            .breakdown-card {
              border: 1px solid #dbe3ef;
              border-radius: 14px;
              overflow: hidden;
              background: #ffffff;
            }

            .breakdown-top {
              height: 6px;
            }

            .breakdown-top.bottle {
              background: #10b981;
            }

            .breakdown-top.restaurant {
              background: #f59e0b;
            }

            .breakdown-body {
              padding: 12px;
            }

            .breakdown-heading {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              gap: 10px;
              margin-bottom: 10px;
            }

            .breakdown-title {
              font-size: 13px;
              font-weight: 800;
              color: #0f172a;
            }

            .breakdown-subtitle {
              font-size: 9.5px;
              color: #64748b;
              margin-top: 3px;
            }

            .margin-pill {
              border-radius: 999px;
              padding: 4px 8px;
              font-size: 9px;
              font-weight: 800;
              background: #ecfdf5;
              color: #047857;
              white-space: nowrap;
            }

            .row {
              display: flex;
              justify-content: space-between;
              gap: 10px;
              border-top: 1px solid #eef2f7;
              padding: 8px 0;
              font-size: 10.5px;
            }

            .row:first-of-type {
              border-top: none;
            }

            .row-label {
              color: #64748b;
            }

            .row-value {
              font-weight: 800;
              color: #0f172a;
            }

            .row-value.cost {
              color: #be123c;
            }

            .row-value.profit-good {
              color: #047857;
            }

            .row-value.profit-bad {
              color: #be123c;
            }

            table {
              width: 100%;
              border-collapse: separate;
              border-spacing: 0;
              table-layout: fixed;
              font-size: 10px;
              border: 1px solid #dbe3ef;
              border-radius: 12px;
              overflow: hidden;
            }

            th {
              background: #eef4ff;
              color: #1e3a8a;
              text-align: left;
              padding: 8px 7px;
              border-bottom: 1px solid #dbe3ef;
              text-transform: uppercase;
              font-size: 8.4px;
              letter-spacing: 0.05em;
              line-height: 1.2;
            }

            td {
              padding: 8px 7px;
              border-bottom: 1px solid #e2e8f0;
              vertical-align: middle;
              line-height: 1.25;
              word-break: break-word;
            }

            tbody tr:nth-child(even) {
              background: #f8fafc;
            }

            tbody tr:last-child td {
              border-bottom: none;
            }

            .note {
              margin-top: 8px;
              font-size: 9.5px;
              color: #64748b;
              line-height: 1.4;
            }

            @media print {
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }

              .report-page {
                width: 100%;
              }
            }
          </style>
        </head>

        <body>
          <div class="report-page">
            <div class="hero">
              <div class="hero-top">
                <div>
                  <div class="eyebrow">Bar Management System</div>
                  <h1 class="title">Profit Report</h1>
                  <div class="subtitle">
                    Business Date: <strong>${escapeHtml(date)}</strong>
                  </div>
                </div>

                <div class="generated">
                  Generated Date & Time
                  <strong>${escapeHtml(generatedOn)}</strong>
                </div>
              </div>
            </div>

            <div class="summary-grid">
              <div class="summary-card">
                <div class="card-label">Overall Sales</div>
                <div class="card-value">${escapeHtml(currency(totalSales))}</div>
              </div>

              <div class="summary-card cost">
                <div class="card-label">Overall Cost</div>
                <div class="card-value">${escapeHtml(currency(totalCost))}</div>
              </div>

              <div class="summary-card profit">
                <div class="card-label">Overall Profit</div>
                <div class="card-value profit-value">${escapeHtml(
                  currency(totalProfit)
                )}</div>
              </div>

              <div class="summary-card margin">
                <div class="card-label">Margin</div>
                <div class="card-value">${escapeHtml(
                  formatPercent(margin)
                )}</div>
              </div>
            </div>

            <div class="section-title">Profit Breakdown</div>

            <div class="breakdown-grid">
              <div class="breakdown-card">
                <div class="breakdown-top bottle"></div>
                <div class="breakdown-body">
                  <div class="breakdown-heading">
                    <div>
                      <div class="breakdown-title">BAR Product Profit</div>
                      <div class="breakdown-subtitle">Liquor and Cigarette sales, both included in BAR totals</div>
                    </div>
                    <div class="margin-pill">
                      ${escapeHtml(
                        formatPercent(
                          bottleSales > 0
                            ? (bottleProfit / bottleSales) * 100
                            : 0
                        )
                      )}
                    </div>
                  </div>

                  <div class="row"><span class="row-label">Liquor Sales</span><span class="row-value">${escapeHtml(currency(liquorSales))}</span></div>
                  <div class="row"><span class="row-label">Cigarette Sales</span><span class="row-value">${escapeHtml(currency(cigaretteSales))}</span></div>

                  <div class="row">
                    <span class="row-label">Sales</span>
                    <span class="row-value">${escapeHtml(
                      currency(bottleSales)
                    )}</span>
                  </div>

                  <div class="row">
                    <span class="row-label">Cost</span>
                    <span class="row-value cost">${escapeHtml(
                      currency(bottleCost)
                    )}</span>
                  </div>

                  <div class="row">
                    <span class="row-label">Profit</span>
                    <span class="row-value ${
                      bottleProfit < 0 ? "profit-bad" : "profit-good"
                    }">${escapeHtml(currency(bottleProfit))}</span>
                  </div>
                </div>
              </div>

              <div class="breakdown-card">
                <div class="breakdown-top restaurant"></div>
                <div class="breakdown-body">
                  <div class="breakdown-heading">
                    <div>
                      <div class="breakdown-title">Restaurant via BAR</div>
                      <div class="breakdown-subtitle">Restaurant sales made through BAR</div>
                    </div>
                  </div>

                  <div class="row">
                    <span class="row-label">Sales</span>
                    <span class="row-value">${escapeHtml(currency(restaurantViaBarSales))}</span>
                  </div>

                  <div class="row">
                    <span class="row-label">Cost</span>
                    <span class="row-value cost">${escapeHtml(currency(restaurantViaBarCost))}</span>
                  </div>

                  <div class="row">
                    <span class="row-label">Profit</span>
                    <span class="row-value ${restaurantViaBarProfit < 0 ? "profit-bad" : "profit-good"}">${escapeHtml(currency(restaurantViaBarProfit))}</span>
                  </div>
                </div>
              </div>

              <div class="breakdown-card">
                <div class="breakdown-top restaurant"></div>
                <div class="breakdown-body">
                  <div class="breakdown-heading">
                    <div>
                      <div class="breakdown-title">Restaurant Direct</div>
                      <div class="breakdown-subtitle">Direct Restaurant sales</div>
                    </div>
                  </div>

                  <div class="row">
                    <span class="row-label">Sales</span>
                    <span class="row-value">${escapeHtml(currency(restaurantDirectSales))}</span>
                  </div>

                  <div class="row">
                    <span class="row-label">Cost</span>
                    <span class="row-value cost">${escapeHtml(currency(restaurantDirectCost))}</span>
                  </div>

                  <div class="row">
                    <span class="row-label">Profit</span>
                    <span class="row-value ${restaurantDirectProfit < 0 ? "profit-bad" : "profit-good"}">${escapeHtml(currency(restaurantDirectProfit))}</span>
                  </div>
                </div>
              </div>

              <div class="breakdown-card">
                <div class="breakdown-top restaurant"></div>
                <div class="breakdown-body">
                  <div class="breakdown-heading">
                    <div>
                      <div class="breakdown-title">Restaurant Total Profit</div>
                      <div class="breakdown-subtitle">Food, beverage and goods sales</div>
                    </div>
                    <div class="margin-pill">
                      ${escapeHtml(
                        formatPercent(
                          restaurantSales > 0
                            ? (restaurantProfit / restaurantSales) * 100
                            : 0
                        )
                      )}
                    </div>
                  </div>

                  <div class="row">
                    <span class="row-label">Sales</span>
                    <span class="row-value">${escapeHtml(
                      currency(restaurantSales)
                    )}</span>
                  </div>

                  <div class="row">
                    <span class="row-label">Cost</span>
                    <span class="row-value cost">${escapeHtml(
                      currency(restaurantCost)
                    )}</span>
                  </div>

                  <div class="row">
                    <span class="row-label">Profit</span>
                    <span class="row-value ${
                      restaurantProfit < 0 ? "profit-bad" : "profit-good"
                    }">${escapeHtml(currency(restaurantProfit))}</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="section-title">14-Day Revenue vs Profit Snapshot</div>

            <table>
              <thead>
                <tr>
                  <th style="width: 25%;">Day</th>
                  <th style="width: 35%;">Revenue</th>
                  <th style="width: 35%;">Profit</th>
                </tr>
              </thead>

              <tbody>
                ${
                  trend
                    .map(
                      (r) => `
                        <tr>
                          <td>${escapeHtml(r.day)}</td>
                          <td>${escapeHtml(currency(r.revenue))}</td>
                          <td>${escapeHtml(currency(r.profit))}</td>
                        </tr>
                      `
                    )
                    .join("") ||
                  `
                    <tr>
                      <td colspan="3" style="text-align:center;">No trend records found.</td>
                    </tr>
                  `
                }
              </tbody>
            </table>

            <div class="note">
              Note: Restaurant cost is calculated using available restaurant batch cost records.
              Non-stock restaurant items may have zero cost unless a cost-tracking rule is added.
            </div>
          </div>

          <script>
            document.title = "${escapeHtml(reportFileName)}";
            window.onload = function () {
              window.print();
            };
          </script>
        </body>
      </html>
    `;

    const printWindow = window.open("", "_blank");

    if (!printWindow) {
      return;
    }

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Profit Dashboard"
        subtitle="Bottle and restaurant sales, cost of goods sold, profit and margin by business date."
        actions={
          <div className="no-print flex flex-col gap-2 sm:flex-row sm:items-end">
            <div>
              <Label className="text-xs text-slate-500">Business date</Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="h-10"
                data-testid="profit-date"
              />
            </div>

            <Button
              onClick={() => load(date)}
              className="bg-navy-800 hover:bg-navy-900 text-white h-10"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>

            <Button
              onClick={printProfitReportPdf}
              className="h-10 bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-sm hover:from-emerald-700 hover:to-teal-700"
              data-testid="profit-print"
            >
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
          </div>
        }
      />

      <div className="bms-card overflow-hidden">
        <div
          className={`p-6 text-white ${
            totalProfit < 0
              ? "bg-gradient-to-br from-rose-700 via-rose-600 to-slate-950"
              : "bg-gradient-to-br from-navy-900 via-emerald-700 to-slate-950"
          }`}
        >
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/70 font-semibold">
                <FileText className="h-3.5 w-3.5" />
                Bar Management System · Profit Report
              </div>

              <h2 className="font-display text-3xl font-bold mt-3">
                {loading ? "…" : currency(totalProfit)}
              </h2>

              <p className="text-sm text-white/75 mt-2">
                Total profit for business date{" "}
                <strong className="text-white">{date}</strong>
              </p>
            </div>

            <div className="rounded-2xl bg-white/10 px-5 py-4 backdrop-blur sm:min-w-[220px]">
              <div className="text-[11px] uppercase tracking-widest text-white/60">
                Margin
              </div>
              <div className="font-display text-3xl font-bold mt-1">
                {loading ? "…" : formatPercent(margin)}
              </div>
              <div className="text-xs text-white/60 mt-1">
                Profit against total sales
              </div>
            </div>
          </div>
        </div>
      </div>

      <section>
      <div className="bms-card overflow-hidden">
        <div className="h-1.5 bg-emerald-600" />
        <div className="p-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-display text-lg font-semibold text-emerald-800">Overall</h3>
            <span className="text-xs text-slate-500">All outlet performance</span>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {[
              ["Sales", totalSales],
              ["Cost", totalCost],
              ["Profit", totalProfit],
            ].map(([label, value]) => (
              <div key={label} className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
                <span className="text-sm text-slate-500">{label}</span>
                <span className={`font-display font-semibold ${label === "Profit" ? "text-emerald-700" : "text-slate-900"}`}>
                  {loading ? "…" : currency(value)}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-3 text-right text-xs text-slate-500">Margin {loading ? "…" : formatPercent(margin)}</div>
        </div>
      </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-display text-lg font-semibold text-slate-800">
            Profit Breakdown
          </h3>

          <span className="text-xs text-slate-500">
            Bottle vs restaurant performance
          </span>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <ProfitBreakdownPanel
            title="BAR Products"
            subtitle="Liquor and Cigarette products; Cigarette sales are included in this BAR total"
            sales={bottleSales}
            cost={bottleCost}
            profit={bottleProfit}
            icon={Wine}
            accentClass="bg-navy-700"
            breakdown={[
              { label: "Liquor Sales", value: liquorSales },
              { label: "Cigarette Sales", value: cigaretteSales },
            ]}
          />
          <div className="bms-card overflow-hidden">
            <div className="h-1.5 bg-orange-500" />
            <div className="p-5">
              <div className="mb-4 flex items-center gap-2">
                <div className="rounded-xl bg-orange-50 p-2"><Utensils className="h-4 w-4 text-orange-700" /></div>
                <div>
                  <h3 className="font-display text-lg font-semibold text-orange-800">RESTAURANT</h3>
                  <p className="mt-1 text-xs text-slate-500">Restaurant-owned financial performance</p>
                </div>
              </div>
              <div className="space-y-4">
                {[
                  { title: "Total", sales: restaurantSales, cost: restaurantCost, profit: restaurantProfit },
                  { title: "Via BAR", sales: restaurantViaBarSales, cost: restaurantViaBarCost, profit: restaurantViaBarProfit },
                  { title: "Direct", sales: restaurantDirectSales, cost: restaurantDirectCost, profit: restaurantDirectProfit },
                ].map((group) => (
                  <div key={group.title} className="border-t border-slate-100 pt-3 first:border-0 first:pt-0">
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-orange-700">{group.title}</div>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                      {[["Sales", group.sales], ["Cost", group.cost], ["Profit", group.profit]].map(([label, value]) => (
                        <div key={label} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 sm:block">
                          <span className="text-xs text-slate-500">{label}</span>
                          <span className={`block font-display text-sm font-semibold ${label === "Profit" ? "text-orange-700" : "text-slate-800"}`}>{loading ? "…" : currency(value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="bms-card overflow-hidden">
          <div className="flex flex-col gap-2 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                14-day view
              </div>
              <h3 className="font-display text-lg font-semibold text-slate-900">
                Revenue vs Profit
              </h3>
            </div>

            <div className="text-xs text-slate-500">
              Based on loaded admin sales data
            </div>
          </div>

          <div className="p-5">
            <ResponsiveContainer width="100%" height={310}>
              <AreaChart data={trend}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1b2f5f" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#1b2f5f" stopOpacity={0} />
                  </linearGradient>

                  <linearGradient id="pro" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>

                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => currency(v)} />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#1b2f5f"
                  fill="url(#rev)"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="profit"
                  stroke="#10b981"
                  fill="url(#pro)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>
    </div>
  );
};
